import { __decorate } from "tslib";
import { Injectable } from "@angular/core";
import Query from "@arcgis/core/rest/support/Query";
import { MapQueryOperator } from "./map-query-operator.model";
import { MapQuery } from "./map-query.model";
import { SessionStorageService } from "../storage/session-storage.service";
import { SessionStorageEnum } from "../storage/session-storage-constants";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import { SharedConstants } from "../../constants/shared.constants";
import StorageUtils from "../../utils/storage-utils";
import { BehaviorSubject } from "rxjs";
import { SearchTableService } from "../table-column-mapping/search-table.service";
import GeometryUtils from "../../../shared/utils/geometry-utils";
import * as query from "@arcgis/core/rest/query";
import { CemeterySearchOption } from "../../../tabs/graves/shared/models/cemetery/cemetery-search-option.enum";
import { FeatureServiceUtils } from "../../utils/feature-service-utils";
let MapSearchService = class MapSearchService {
    sessionStorageService;
    searchTableService;
    searchType;
    mapLocation = new BehaviorSubject("");
    mapLocationAsObservable = this.mapLocation.asObservable();
    constructor(sessionStorageService, searchTableService) {
        this.sessionStorageService = sessionStorageService;
        this.searchTableService = searchTableService;
    }
    changeMapLocation(selectedPropertyId, height) {
        this.mapLocation.next(selectedPropertyId);
        this.searchTableService.changeContainerHeight(height);
    }
    async doSearchWithAdditionalColumn(url, text, column, returnGeometry, additionalColumnValue, additionalColumn) {
        const featureSet = await this.searchItemByAttribute(url, text, column, returnGeometry, false, null, true, additionalColumnValue, additionalColumn);
        return this.transformFeatureSet(featureSet);
    }
    async doSearchWithAdditionalColumns(url, text, column, returnGeometry, additionalColumnValue, additionalColumn) {
        const featureSet = await this.searchItemByAttribute(url, text, column, returnGeometry, true, null, true, additionalColumnValue, additionalColumn);
        return this.transformFeatureSet(featureSet);
    }
    async doSearchSectionalTitleColumn(url, text, column, returnGeometry, additionalColumnValue, additionalColumn) {
        const featureSet = await this.searchItemByAttribute(url, text, column, returnGeometry, false, null, true, additionalColumnValue, additionalColumn);
        const searchTypeList = new Array();
        if (!featureSet) {
            return searchTypeList;
        }
        featureSet.features.forEach((feature) => {
            const searchItem = {
                id: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "OBJECTID", "objectid"),
                erfNumber: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "ErfNo", "erfno"),
                address: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "Address", "address"),
                allotment: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "Allotment", "allotment"),
                propertyDescription: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "PropertyDescription", "propertydescription"),
                sgCode: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "SgCode", "sgcode"),
                farmNumber: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "FarmNo", "farmno"),
                ptnNum: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "PtnNum", "ptnnum"),
                zoning: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "Zoning", "zoning"),
                geometry: feature.geometry ? GeometryUtils.mapGeometryToDTO(feature.geometry) : null,
                totalValue: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "TotalValue", "totalvalue"),
                buildingUse: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "BuildingUse", "buildinguse"),
                erfSize: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "ErfSize", "erfsize"),
                buildingFloorArea: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "BuildingFloorArea", "buildingfloorarea"),
                titleDeed: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "TitleDeed", "titledeed"),
                unitNumber: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "UnitNumber", "unitnumber"),
                sectionalTitleId: FeatureServiceUtils.PropertyDataAlternateColumnName(feature.attributes, "SectionalTitleSchemeID", "sectionaltitleschemeid"),
            };
            searchTypeList.push(searchItem);
        });
        return searchTypeList;
    }
    async doSearch(url, text, column, returnGeometry, searchOption, returnTopOne, returnAllColumns) {
        let featureSet;
        if (text) {
            // @ts-ignore
            if (column && column === SharedConstants.SEARCH_BY_DEFAULT) {
                featureSet = await this.searchAllItems(url, returnGeometry, searchOption);
            }
            else {
                // assert type
                column = column;
                featureSet = await this.searchItemByAttribute(url, text, column, returnGeometry, returnTopOne, searchOption, returnAllColumns);
            }
        }
        else {
            featureSet = await this.searchAllItems(url, returnGeometry);
        }
        return this.transformFeatureSet(featureSet, searchOption);
    }
    async searchForCemeteries(url) {
        const featureSet = await this.searchAllItems(url, false, CemeterySearchOption.Cemetery, false, true);
        return this.transformFeatureSet(featureSet, CemeterySearchOption.Cemetery);
    }
    getSelectedSystemModule() {
        const selectedModuleID = this.sessionStorageService.get(SessionStorageEnum.SelectedModuleID);
        // @ts-ignore
        return StorageUtils.convertSessionStorageStringToNumber(selectedModuleID);
    }
    transformFeatureSet(featureSet, searchOption) {
        const searchTypeList = new Array();
        if (!featureSet) {
            return searchTypeList;
        }
        featureSet.features.forEach((feature) => {
            const searchItem = this.transformFeature(feature, searchOption);
            if (this.checkValidSearchItem(searchItem)) {
                searchTypeList.push(searchItem);
            }
        });
        return searchTypeList;
    }
    // TODO remove this when we include this in the where clause of the map search
    checkValidSearchItem(searchItem) {
        let valid = false;
        Object.keys(searchItem).forEach((key) => {
            // @ts-ignore
            if (searchItem[key] && searchItem[key] !== "" && searchItem[key] !== " ") {
                valid = true;
            }
        });
        return valid;
    }
    async searchAllItems(url, returnGeometry, searchOption, returnTopOne, returnAllColumns) {
        return query.executeQueryJSON(url, this.buildQuery("1=1", this.getAttributeListForSearchType(searchOption), returnGeometry, returnTopOne, returnAllColumns));
    }
    async searchItemByAttribute(url, text, column, returnGeometry, returnTopOne, searchOption, returnAllColumns, additionalColumnValue, additionalColumn) {
        let where = returnTopOne ? MapQuery.doOperation(column, text, MapQueryOperator.EqualsTo) : MapQuery.doOperation(column, text, MapQueryOperator.Like);
        if (additionalColumn && additionalColumnValue) {
            where += ` AND ${MapQuery.doOperation(additionalColumn, additionalColumnValue, MapQueryOperator.EqualsTo)}`;
        }
        return query.executeQueryJSON(url, this.buildQuery(where, this.getAttributeListForSearchType(searchOption), returnGeometry, returnTopOne, returnAllColumns));
    }
    buildQuery(whereClause, outFields, returnGeometry, returnTopOne, returnAllColumns) {
        const newQuery = new Query();
        newQuery.where = whereClause;
        newQuery.returnGeometry = returnGeometry;
        // @ts-ignore
        // newQuery.outFields = returnAllColumns ? "*" : outFields;
        newQuery.outFields = "*";
        if (returnTopOne) {
            newQuery.maxRecordCountFactor = 1;
        }
        newQuery.outSpatialReference = new SpatialReference({
            wkid: 102100
        });
        return newQuery;
    }
    static ctorParameters = () => [
        { type: SessionStorageService },
        { type: SearchTableService }
    ];
};
MapSearchService = __decorate([
    Injectable({
        providedIn: "root"
    })
], MapSearchService);
export { MapSearchService };
